import type { Dayjs } from 'dayjs'
import { DEFAULT_NEXT_DAY_BOOKING_CUTOFF_TIME } from '../../../constants/configuration.constants'
import { nowInAppTimezone } from '../../../utils/dateTime.utils'
/**
 * This utility file provides date-related constants and functions
 * for the Datepicker component.
 *
 * - `defaultDate`: A Dayjs object representing the default date for the Datepicker.
 *   If the current time is on/after the configured cutoff, it defaults to the day after tomorrow.
 *   Otherwise, it defaults to tomorrow.
 */

const [DEFAULT_CUTOFF_HOUR, DEFAULT_CUTOFF_MINUTE] = DEFAULT_NEXT_DAY_BOOKING_CUTOFF_TIME.split(':').map((value) => Number(value))

const parseCutoffTime = (cutoffTime: string) => {
  const match = /^([01]\d|2[0-3]):([0-5]\d)$/.exec(cutoffTime)
  if (!match) {
    return { hour: DEFAULT_CUTOFF_HOUR, minute: DEFAULT_CUTOFF_MINUTE }
  }

  return {
    hour: Number(match[1]),
    minute: Number(match[2]),
  }
}

const isOnOrAfterCutoff = (now: Dayjs, cutoffTime: string) => {
  const { hour, minute } = parseCutoffTime(cutoffTime)
  const todayCutoff = now.startOf('day').hour(hour).minute(minute).second(0).millisecond(0)
  return now.valueOf() >= todayCutoff.valueOf()
}

export const getMinBookableDate = (cutoffTime: string = DEFAULT_NEXT_DAY_BOOKING_CUTOFF_TIME) => {
  const now = nowInAppTimezone()
  return isOnOrAfterCutoff(now, cutoffTime) ? now.add(2, 'day').startOf('day') : now.add(1, 'day').startOf('day')
}

export const getBookingDefaultDate = (cutoffTime: string = DEFAULT_NEXT_DAY_BOOKING_CUTOFF_TIME) => {
  return getMinBookableDate(cutoffTime)
}

export const bookingDefaultDate = getBookingDefaultDate()
